import { globalObservability } from '../global';
import { getDeployEnv } from './deployEnv';

export interface Segment extends Record<string, (...args: any[]) => void> {
  trackSubmit: (...args: any[]) => void;
  trackClick: (...args: any[]) => void;
  trackLink: (...args: any[]) => void;
  trackForm: (...args: any[]) => void;
  pageview: (...args: any[]) => void;
  identify: (...args: any[]) => void;
  reset: (...args: any[]) => void;
  group: (...args: any[]) => void;
  track: (...args: any[]) => void;
  ready: (...args: any[]) => void;
  alias: (...args: any[]) => void;
  debug: (...args: any[]) => void;
  page: (...args: any[]) => void;
  once: (...args: any[]) => void;
  off: (...args: any[]) => void;
  on: (...args: any[]) => void;
  addSourceMiddleware: (...args: any[]) => void;
  addIntegrationMiddleware: (...args: any[]) => void;
  setAnonymousId: (...args: any[]) => void;
  addDestinationMiddleware: (...args: any[]) => void;
}
const SEGMENT_METHODS = [
  'trackSubmit',
  'trackClick',
  'trackLink',
  'trackForm',
  'pageview',
  'identify',
  'reset',
  'group',
  'track',
  'ready',
  'alias',
  'debug',
  'page',
  'once',
  'off',
  'on',
  'addSourceMiddleware',
  'addIntegrationMiddleware',
  'setAnonymousId',
  'addDestinationMiddleware',
];

export const RESET_PASSWORD_TOKEN_REGEX = /reset_password_token=[a-zA-Z0-9_\-.]+?(?=&|$)/;
export const ACCESS_TOKEN_REGEX = /access_token=[a-zA-Z0-9_\-.]+?(?=&|$)/;

type SegmentStub = Segment & {
  SNIPPET_VERSION?: string;
  _loadOptions?: unknown;
  factory?: (method: string) => (...args: any[]) => SegmentStub;
  push: (args: unknown[]) => number;
};

const redactUrl = (value: string) =>
  value
    .replace(RESET_PASSWORD_TOKEN_REGEX, 'reset_password_token=REDACTED')
    .replace(ACCESS_TOKEN_REGEX, 'access_token=REDACTED');

const ensureSegmentStub = (): Segment | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const existingAnalytics = (window as any).analytics as Segment | undefined;
  if (existingAnalytics) {
    return existingAnalytics;
  }

  // This mirrors Segment's queueing snippet, but runs only when analytics is
  // actually used so importing observability stays free of browser mutations.
  const segment = [] as unknown as SegmentStub;
  (window as any).analytics = segment;

  segment.factory = function (method: string) {
    return function (...args: any[]) {
      segment.push([method, ...args]);
      return segment;
    };
  };

  for (const key of SEGMENT_METHODS) {
    segment[key] = segment.factory(key);
  }

  segment.SNIPPET_VERSION = '4.15.2';
  // We don't want local storage fallbacks for our ajs_anonymous_id and
  // ajs_user_id cookies because local storage isn't sent with server requests.
  segment._loadOptions = { user: { localStorageFallbackDisabled: true } };
  segment.page({
    url: redactUrl(window.location.href),
    search: redactUrl(window.location.search),
  });

  return segment;
};

// The segment script overwrites window.analytics with a new object, so we can't
// directly export the `segment` object. Instead, export an object that delegates
// to `window.analytics`
const segmentWrapper = {} as Segment;
SEGMENT_METHODS.forEach((methodName) => {
  segmentWrapper[methodName] = async function (...args: any[]) {
    const analytics = ensureSegmentStub();
    if (!analytics) {
      return;
    }

    try {
      await analytics[methodName](...args);
    } catch (e) {
      // when we fail to send to segment, we want to log this error to sentry
      let error = new Error('Error sending to Segment');
      let extraErrorData: {
        method: string;
        event?: string;
        value?: string;
        team?: string;
        pathname?: string;
      } = {
        method: methodName,
      };
      if (methodName === 'track') {
        error = new Error('Failed to send Segment Event or Error');
        extraErrorData['event'] = args[0];
        extraErrorData['value'] = args[1]?.value;
        extraErrorData['team'] = args[1]?.team || '';
        extraErrorData['pathname'] = args[1]?.pathname || '';
      }
      globalObservability.getSentryClient().withScope?.((scope) => {
        scope.setExtras({
          error: e,
          ...extraErrorData,
        });
        globalObservability.getSentryClient().captureException?.(error);
      });
    }
  };
});

// Define a method to load Analytics.js from segment CDN
//
// The default source that tracking events are sent to is
// Web Market JS Prod (DWH: web_js_production) in production and
// Web Market JS Staging (DWH: web_js_staging) in staging.
// The request to load the script is proxied via Cloudflare Worker
// in order to circumvent ad blockers.
//
// To send tracking events to other sources, pass the source's
// write key to this function. The request to load the script
// may be blocked by ad blockers.
export const loadSegment = (key?: string, lazyLoad?: boolean) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const injectScript = () => {
    ensureSegmentStub();

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    if (key) {
      script.src =
        ('https:' === document.location.protocol ? 'https://' : 'http://') +
        'trkcdn.opendoor.com/analytics.js/v1/' +
        key +
        '/analytics.min.js';
    } else {
      script.src =
        ('https:' === document.location.protocol ? 'https://' : 'http://') +
        (getDeployEnv() === 'production' ? 'www.opendoor.com' : 'demo.simplersell.com') +
        '/_scripts/sgm.min.js';
    }

    // Insert our script next to the first script element.
    const first = document.getElementsByTagName('script')[0];
    // @ts-ignore
    first.parentNode.insertBefore(script, first);
  };

  if (lazyLoad) {
    window.addEventListener('load', injectScript);
  } else {
    injectScript();
  }
};

export default segmentWrapper;
